import { ArrowLeft20 } from '@carbon/icons-react';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import {
  CpBody03Placeholder,
  CpHeading03Primary,
} from 'components/atoms/customer-portal/typography';
import { useGetBackground } from 'components/organisms/customer-portal/types/utils';
import { useIsResponsive, useIsTablet } from 'hooks/use-media-query';
import { CpSectionWrapper as CpSectionWrapperAtom } from '../../common';
import { useCarouselClasses } from '../../common/hooks';
import { CpImageGalleryVariantWidgetProps } from '../types';
import { ControlButton, RightArrow } from './image-gallery-carousel';
import 'swiper/css';

const Wrapper = styled.div<{ areImagesAvailable }>`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: flex-start;
  overflow: hidden;
  gap: var(--cp-spacing-06);

  .swiper {
    width: 100%;
    height: 50%;
    margin: 0;
  }

  .swiper-slide {
    text-align: center;
    font-size: 18px;
    background: #fff;
    /* width: 330px !important; */

    /* Center slide text vertically */
    display: flex;
    justify-content: center;
    align-items: center;
    height: auto;
    margin-right: 24px;
    /* width: 30% !important; */
    /* margin: auto; */
  }

  .swiper-slide img {
    display: block;
    width: 18rem;
    height: 18rem;
    object-fit: cover;
    object-position: center;
  }

  .swiper-pagination-bullets {
    margin-top: var(--cp-spacing-09);
    display: flex;
    justify-content: start;
    gap: var(--cp-spacing-03);
  }

  .swiper-pagination-bullet {
    display: ${({ areImagesAvailable }) =>
      areImagesAvailable ? 'block' : 'none'};
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--cp-background-as-black-opacity);
    cursor: pointer !important;
  }

  .swiper-pagination-bullet-active {
    background-color: black;
  }
`;

const CpSectionWrapper = styled(CpSectionWrapperAtom)`
  padding-right: 0;
  position: relative;

  @media (max-width: 480px) {
    padding-right: var(--cp-spacing-05);
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  padding-right: max(var(--spacing-10), calc(calc(100% - 1080px)));

  @media (max-width: 480px) {
    gap: var(--cp-spacing-03);
  }
`;

const Title = styled(CpHeading03Primary)`
  margin-bottom: 0;
`;

const Description = styled(CpBody03Placeholder)`
  margin-bottom: 0;
`;

const CarouselControlWrapper = styled.div<{ areImagesAvailable: boolean }>`
  display: ${({ areImagesAvailable }) =>
    areImagesAvailable ? 'flex' : 'none'};
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-end;
  margin-right: 14rem;
  position: absolute;
  /* left: 1560px; */
  /* top: 600px; */
  gap: var(--cp-spacing-06);
  width: 100%;
  z-index: 10;
  /* margin-right: 14rem; */
  position: absolute;
  right: -120px;
  bottom: 65px;
  z-index: 100;

  @media (max-width: 768px) {
    left: -30px;
    top: 445px;
    gap: var(--cp-spacing-06);
  }

  @media (max-width: 480px) {
    top: 490px;
    left: -1rem;
  }
`;

export const CpImageGalleryRowCarousel: React.FC<CpImageGalleryVariantWidgetProps> =
  ({ disabled, data, widget }) => {
    const background = useGetBackground(widget);
    // temporary fix for swiper issue, will figure this out
    const [swiperRef, setSwiperRef] = useState<SwiperClass>();
    const [current, setCurrent] = useState(0);
    const { nextClass, previousClass, swiper, nextElement, previousElement } =
      useCarouselClasses();

    const isTablet = useIsTablet();
    const isResponsive = useIsResponsive();

    const slidesPerView = isResponsive ? 1 : isTablet ? 3 : 4;

    const handlePrevious = () => {
      let slideTo;

      const paginationElement = document.querySelectorAll('.swiper-pagination');

      const totalIndicators = paginationElement[1]?.children.length;

      if (current - 1 === -1) {
        slideTo = totalIndicators - 1;
      } else {
        slideTo = current - 1;
      }
      setCurrent(slideTo);

      swiperRef?.slideTo(slideTo);
    };

    const handleNext = () => {
      let slideTo;
      const paginationElement = document.querySelectorAll('.swiper-pagination');

      const totalIndicators = paginationElement[1]?.children.length;

      if (current + 1 === totalIndicators) {
        slideTo = 0;
      } else {
        slideTo = current + 1;
      }
      setCurrent(slideTo);
      swiperRef?.slideTo(slideTo);
    };

    const areImagesAvailable = data?.images?.length > 0;

    return (
      <CpSectionWrapper background={background} isDisabled={disabled}>
        <Wrapper areImagesAvailable={areImagesAvailable}>
          <TitleWrapper>
            <Title
              autoAdjustColor
              data-cy="cp.image_gallery_row_carousel.title"
            >
              {data?.title}
            </Title>
            <Description
              autoAdjustColor
              data-cy="cp.image_gallery_row_carousel.description"
            >
              {data?.description}
            </Description>
          </TitleWrapper>
          <Swiper
            freeMode
            onSwiper={setSwiperRef}
            pagination={{
              clickable: true,
            }}
            onSlideNextTransitionEnd={() => {
              setCurrent(swiperRef?.activeIndex ?? 0);
            }}
            slidesPerView={slidesPerView}
            spaceBetween={24}
            autoplay={{
              delay: 2000,
              disableOnInteraction: true,
            }}
            modules={[Pagination, Autoplay, Navigation]}
            className={swiper}
            grabCursor
            navigation={{
              enabled: true,
              nextEl: nextElement,
              prevEl: previousElement,
            }}
          >
            {data?.images?.map((i, index) => {
              return (
                <SwiperSlide className="rl-cp-carousel-image">
                  <img
                    data-cy={`cp.image_gallery_row_carousel.image_${index}`}
                    src={i?.attachment?.location}
                    alt=""
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
          <CarouselControlWrapper areImagesAvailable={areImagesAvailable}>
            <ControlButton
              className={previousClass}
              icon={<ArrowLeft20 className="cp-svg-auto-adjust-fill" />}
              onClick={handlePrevious}
            />
            <ControlButton
              className={nextClass}
              icon={<RightArrow className="cp-svg-auto-adjust-fill" />}
              onClick={handleNext}
            />
          </CarouselControlWrapper>
        </Wrapper>
      </CpSectionWrapper>
    );
  };
